import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton // For the X close button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable'; // For Scheduled
import EventBusyIcon from '@mui/icons-material/EventBusy';       // For No Show
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // For Interviewed
import CheckIcon from '@mui/icons-material/Check'; // For the active status indicator

import { InterviewStatus } from "../types";
// import { InterviewStatusButton } from "../InterviewStatusButton"; // No longer using this directly

// Styles will be moved to a dedicated .module.css or .styles.ts for ApplicationActions
// For now, some inline/sx might be used, or we assume styles will be created.
import styles from './ApplicationActions.module.css'; // Create this file

interface ApplicationActionsProps {
    open: boolean; // Added to control Dialog visibility
    currentInterviewStatus: InterviewStatus; // Added to know the current status
    onClose: () => void;
    onNoShowButtonClick: () => void; // This might become just onSetInterviewStatus(InterviewStatus.noshow)
    onSetInterviewStatus: (newInterviewStatus: InterviewStatus) => Promise<void>;
}

const ApplicationActions: React.FC<ApplicationActionsProps> = ({
    open,
    currentInterviewStatus, // Destructure new prop
    onClose,
    onNoShowButtonClick, // Consider simplifying if "No Show" button directly calls onSetInterviewStatus
    onSetInterviewStatus
}) => {
    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            PaperProps={{
                 className: styles.dialogPaper,
                 style: { zIndex: 1302 } // High z-index, slightly different from other dialog if needed for stacking
            }}
            sx={{ zIndex: 1302 }} // Ensure backdrop is also high
            disablePortal // Add this to see if it resolves context issues
            maxWidth="xs" 
            fullWidth
        >
            <DialogTitle className={styles.dialogTitle}>
                Update Interview Status
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    className={styles.closeButton}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
                <DialogContentText className={styles.descriptionText}>
                    Updating will also allow the candidate access to your contact details if they don't already have this.
                </DialogContentText>
                <div className={styles.buttonContainer}>
                    <Button
                        variant={currentInterviewStatus === InterviewStatus.scheduled ? "contained" : "outlined"} // Contained if active, else outlined
                        className={`${styles.statusButton} ${styles.scheduledButton}`}
                        startIcon={<EventAvailableIcon />}
                        endIcon={currentInterviewStatus === InterviewStatus.scheduled ? <CheckIcon /> : null}
                        onClick={() => onSetInterviewStatus(InterviewStatus.scheduled)}
                    >
                        Interview Scheduled
                    </Button>
                    <Button
                        variant={currentInterviewStatus === InterviewStatus.interviewed ? "contained" : "outlined"}
                        className={`${styles.statusButton} ${styles.interviewedButton}`}
                        startIcon={<CheckCircleOutlineIcon />}
                        endIcon={currentInterviewStatus === InterviewStatus.interviewed ? <CheckIcon /> : null}
                        onClick={() => onSetInterviewStatus(InterviewStatus.interviewed)}
                    >
                        Interviewed
                    </Button>
                    <Button
                        variant="outlined" // No show is never "active" in the same way
                        className={`${styles.statusButton} ${styles.noShowButton}`}
                        startIcon={<EventBusyIcon />}
                        endIcon={currentInterviewStatus === InterviewStatus.noshow ? <CheckIcon sx={{ color: styles.noShowButtonIconActiveColor }} /> : null} // Special color for no-show check
                        onClick={onNoShowButtonClick} 
                    >
                        No show
                    </Button>
                </div>
            </DialogContent>
            {/* No DialogActions needed if buttons are in content */}
        </Dialog>
    );
};

export default ApplicationActions;
